import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, Plus, DollarSign, Workflow, MoreHorizontal } from 'lucide-react'
import { cn } from '../../utils/cn'
import { useAccessControl } from '../../contexts/AccessControlContext'
import { getNavKeyForHref } from '../../config/navigationCatalog'

interface BottomNavProps {
 onMenuOpen: () => void
}

export const BottomNav: React.FC<BottomNavProps> = ({ onMenuOpen }) => {
 const { t } = useTranslation()
 const location = useLocation()
 const navigate = useNavigate()
 const { canAccess } = useAccessControl()

 const isActive = (href: string | null) => {
 if (!href) return false
 if (href === '/') return location.pathname === '/'
 if (href === '/financial/sales') return location.pathname.startsWith('/financial/sales')
 if (href === '/operation/post-sale') return location.pathname.startsWith('/operation/post-sale')
 if (href === '/post-sales/pipeline') return location.pathname.startsWith('/post-sales/pipeline')
 return location.pathname.startsWith(href)
 }

 const navItems: Array<{
 label: string
 icon: typeof LayoutDashboard
 href: string | null
 }> = [
 { label: t('nav.dashboard'), icon: LayoutDashboard, href: '/' },
 { label: t('nav.salesSub.closedDeals'), icon: DollarSign, href: '/financial/sales' },
 { label: t('nav.postSalesSub.salesPipeline'), icon: Workflow, href: '/post-sales/pipeline' },
 { label: t('nav.more'), icon: MoreHorizontal, href: null },
 ].filter(item => {
 if (!item.href) return true
 const key = getNavKeyForHref(item.href)
 return Boolean(key && canAccess(key))
 })

 return (
 <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card dark:bg-primary-900 border-t border-border safe-area-bottom">
 <div className="flex items-center justify-around h-16 px-2">

 {navItems.slice(0, 2).map((item) => {
 const active = isActive(item.href)
 return (
 <button
 key={item.label}
 type="button"
 onClick={() => item.href && navigate(item.href)}
 className={cn(
 'flex flex-col items-center justify-center gap-0.5 flex-1 h-full min-w-0',
 'transition-colors duration-150',
 active
 ? 'text-accent-600 dark:text-accent-400'
 : 'text-accent hover:text-primary-700 dark:hover:text-primary-200'
 )}
 >
 <item.icon className="h-5 w-5 shrink-0" />
 <span className="text-[0.625rem] font-medium leading-tight text-center px-0.5 truncate max-w-full">{item.label}</span>
 </button>
 )
 })}

 <button
 type="button"
 onClick={() => navigate('/leads/new')}
 className={cn(
 'flex flex-col items-center justify-center flex-shrink-0 mx-1',
 'w-14 h-14 -mt-5 rounded-full',
 'bg-accent-600 text-white shadow-lg shadow-accent-600/30',
 'hover:bg-accent-700 active:scale-95',
 'transition-all duration-150',
 'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2'
 )}
 aria-label={t('nav.newLead')}
 >
 <Plus className="h-6 w-6" />
 </button>

 {navItems.slice(2).map((item) => {
 if (!item.href) {
 return (
 <button
 key={item.label}
 type="button"
 onClick={onMenuOpen}
 className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full min-w-0 text-accent hover:text-primary-700 dark:hover:text-primary-200 transition-colors duration-150"
 >
 <item.icon className="h-5 w-5 shrink-0" />
 <span className="text-[0.625rem] font-medium truncate max-w-full px-0.5">{item.label}</span>
 </button>
 )
 }

 const active = isActive(item.href)
 return (
 <button
 key={item.label}
 type="button"
 onClick={() => navigate(item.href!)}
 className={cn(
 'flex flex-col items-center justify-center gap-0.5 flex-1 h-full min-w-0',
 'transition-colors duration-150',
 active
 ? 'text-accent-600 dark:text-accent-400'
 : 'text-accent hover:text-primary-700 dark:hover:text-primary-200'
 )}
 >
 <item.icon className="h-5 w-5 shrink-0" />
 <span className="text-[0.625rem] font-medium leading-tight text-center px-0.5 truncate max-w-full">{item.label}</span>
 </button>
 )
 })}
 </div>
 </nav>
 )
}
